import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Order, OrderService, OrderStatus, ProductMap, MachineInfo } from "../api/api_pb";
import { useClient } from "../connect_api";
import { toast } from "react-toastify";
import CurrencyFormatter from "../utils/currencyFormatter";
import { convertTimestampToDateWithTimezone } from "../utils/dateUtils";
import RelativeTime from "../components/RelativeTime";
import Papa from "papaparse";
import { useAuth } from "../AuthContext";
import { Link } from "react-router-dom";
import { useGlobalContext } from '../GlobalContext';
import { 
    Download, 
    Package,
    RotateCcw,
    CheckCircle,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    ArrowLeft,
    FileText,
    Clock
} from 'lucide-react';
import { AuthMethod } from "../common/v1/types_pb";

interface LocationState {
    machine: MachineInfo;
    productMap: ProductMap;
    doorIdDisplay: string;
}

const CellTransactionHistory: React.FC = () => {
    const { machineUuid, mdbItemNumber } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const state = location.state as LocationState;
    const { updateCustomers } = useGlobalContext();
    
    // Get page from URL params, default to 1
    const pageParam = searchParams.get('page');
    const currentPage = pageParam ? Math.max(1, parseInt(pageParam, 10)) : 1;
    
    const [orders, setOrders] = useState<Order[]>([]);
    const [itemsPerPage] = useState<number>(12);
    const [totalOrders, setTotalOrders] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list'); // Default to list view
    const { organizationInfo } = useAuth();
    const orderClient = useClient(OrderService);

    const fetchOrders = useCallback(async () => {
        // Early return if critical data is not available
        if (!machineUuid || !mdbItemNumber) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // Use the ListCellOrders API for efficient cell-specific queries
            const response = await orderClient.listCellOrders({
                machineUuid: machineUuid,
                mdbItemNumber: BigInt(mdbItemNumber),
                page: currentPage - 1, // API uses 0-based pagination
                limit: itemsPerPage
            });

            setOrders(response.orders);
            // Since the API doesn't return total count, we need to estimate
            // If we get less than the requested limit, we're on the last page
            const isLastPage = response.orders.length < itemsPerPage;
            if (isLastPage) {
                // We know the exact total
                setTotalOrders((currentPage - 1) * itemsPerPage + response.orders.length);
            } else {
                // We don't know the exact total, just show current range
                // We'll use a special value to indicate unknown total
                setTotalOrders(-1); // -1 indicates unknown total
            }
        } catch (err) {
            setError('Failed to fetch transaction history');
            console.error('Error fetching orders:', err);
            toast.error('Failed to fetch transaction history');
        } finally {
            setIsLoading(false);
        }
    }, [orderClient, currentPage, itemsPerPage, machineUuid, mdbItemNumber]);

    useEffect(() => {
        // Fetch when we have the machine UUID and MDB item number
        if (machineUuid && mdbItemNumber) {
            fetchOrders();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]); // Only re-fetch when page changes

    const downloadCsv = async () => {
        if (!machineUuid || !mdbItemNumber) {
            toast.error('Missing required information');
            return;
        }

        setIsLoading(true);

        try {
            // Fetch all orders for this cell (using pagination)
            let allOrders: Order[] = [];
            let page = 0;
            let hasMore = true;
            
            while (hasMore) {
                const response = await orderClient.listCellOrders({
                    machineUuid: machineUuid,
                    mdbItemNumber: BigInt(mdbItemNumber),
                    page: page,
                    limit: 100 // Fetch 100 at a time
                });
                
                allOrders = [...allOrders, ...response.orders];
                hasMore = response.orders.length === 100;
                page++;
                
                // Safety limit to prevent infinite loops
                if (page > 100) break;
            }

            const CurrencyFormatterWith = (priceInCents: number, currencyDecimalPoint: number) => {
                const divisor = Math.pow(10, currencyDecimalPoint);
                const roundedValue = Number((priceInCents / divisor).toFixed(currencyDecimalPoint));

                return `${roundedValue.toLocaleString(undefined, {
                    minimumFractionDigits: currencyDecimalPoint,
                    maximumFractionDigits: currencyDecimalPoint,
                })}`;
            };

            const csvData = allOrders.map(order => ({
                "Created At": order.createdAt ? convertTimestampToDateWithTimezone(order.createdAt, organizationInfo?.timezone ?? '') : '',
                "Order UUID": order.uuid,
                "Status": OrderStatus[order.status],
                "Auth Method": getAuthMethodDisplay(order.authMethod),
                "Customer": order.customer?.mobileNumber || '',
                "Product": order.productName,
                "Price": order.priceInCents ? CurrencyFormatterWith(order.priceInCents, organizationInfo?.currencyDecimalPoint ?? 0) : '',
                "Machine": order.machineName,
                "Door": order.doorIdDisplay,
                "Trackable Item": order.trackableItemName,
                "Dispensed At": order.dispensedAt ? convertTimestampToDateWithTimezone(order.dispensedAt, organizationInfo?.timezone ?? '') : '',
                "Returned At": order.returnedAt ? convertTimestampToDateWithTimezone(order.returnedAt, organizationInfo?.timezone ?? '') : '',
                "Created At (UTC)": order.createdAt ? convertTimestampToDateWithTimezone(order.createdAt, 'UTC') : '',
                "Dispensed At (UTC)": order.dispensedAt ? convertTimestampToDateWithTimezone(order.dispensedAt, 'UTC') : '',
                "Returned At (UTC)": order.returnedAt ? convertTimestampToDateWithTimezone(order.returnedAt, 'UTC') : '',
            }));

            const csv = Papa.unparse(csvData);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `cell_transactions_${mdbItemNumber}_${Date.now()}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error downloading CSV:', err);
            toast.error('Failed to download CSV');
        } finally {
            setIsLoading(false);
        }
    };

    // When totalOrders is -1 (unknown), we don't know the total pages
    // We show pagination controls based on whether we have a full page of results
    const totalPages = totalOrders === -1 ? 
        (orders.length === itemsPerPage ? currentPage + 1 : currentPage) : 
        Math.ceil(totalOrders / itemsPerPage);

    const getStatusColor = (status: OrderStatus) => {
        switch (status) {
            case OrderStatus.PENDING_PAYMENT:
                return 'bg-yellow-50 text-yellow-700 border-yellow-200';
            case OrderStatus.PAID:
                return 'bg-green-50 text-green-700 border-green-200';
            case OrderStatus.DISPENSED:
                return 'bg-blue-50 text-blue-700 border-blue-200';
            case OrderStatus.CANCELED:
                return 'bg-red-50 text-red-700 border-red-200';
            case OrderStatus.DISPENSE_FAILURE:
                return 'bg-red-50 text-red-700 border-red-200';
            case OrderStatus.PAYMENT_FAILURE:
                return 'bg-orange-50 text-orange-700 border-orange-200';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-200';
        }
    };

    const getStatusIcon = (status: OrderStatus) => {
        switch (status) {
            case OrderStatus.PENDING_PAYMENT:
                return <Clock className="w-3 h-3" />;
            case OrderStatus.PAID:
                return <CheckCircle className="w-3 h-3" />;
            case OrderStatus.DISPENSED:
                return <Package className="w-3 h-3" />;
            case OrderStatus.CANCELED:
                return <RotateCcw className="w-3 h-3" />;
            default:
                return <AlertCircle className="w-3 h-3" />;
        }
    };

    const getAuthMethodDisplay = (authMethod: AuthMethod) => {
        switch (authMethod) {
            case AuthMethod.MOBILE_PIN:
                return 'Mobile+PIN';
            case AuthMethod.VENUE_ACCESS_CODE:
                return 'Access Code';
            case AuthMethod.UNSPECIFIED:
            default:
                return 'Unknown';
        }
    };

    const handleShowleasesClick = (customer: any) => {
        if (customer) {
            updateCustomers(customer);
            navigate(`/show-lease/${customer.uuid}`);
        }
    };

    const handlePageChange = (newPage: number) => {
        const newParams = new URLSearchParams(searchParams);
        if (newPage === 1) {
            newParams.delete('page'); // Remove page param for page 1
        } else {
            newParams.set('page', newPage.toString());
        }
        setSearchParams(newParams);
        window.scrollTo(0, 0);
    };

    // Order Card Component for Grid View
    const OrderCard = ({ order }: { order: Order }) => (
        <div className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100">
            {/* Status Badge */}
            <div className="flex justify-between items-start mb-4">
                <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                    {getStatusIcon(order.status)}
                    {OrderStatus[order.status]}
                </span>
                <span className="text-xs text-gray-500">{getAuthMethodDisplay(order.authMethod)}</span>
            </div>

            {/* Order Details */}
            <div className="space-y-3">
                <div>
                    <p className="text-xs text-gray-600 mb-0.5">Customer</p>
                    <Link
                        to={`/show-lease/${order.customer?.uuid}`}
                        onClick={() => handleShowleasesClick(order.customer)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                    >
                        {order.customer?.mobileNumber || 'Unknown'}
                    </Link>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <p className="text-xs text-gray-600 mb-0.5">Product</p>
                        <p className="text-sm font-medium text-gray-900">{order.productName}</p>
                    </div>
                    <div>
                        <p className="text-xs text-gray-600 mb-0.5">Price</p>
                        <p className="text-sm font-bold text-gray-900">
                            <CurrencyFormatter priceInCents={order.priceInCents} />
                        </p>
                    </div>
                </div>

                <div>
                    <p className="text-xs text-gray-600 mb-0.5">Trackable Item</p>
                    <p className="text-sm text-gray-900">{order.trackableItemName || 'N/A'}</p>
                </div>

                <div className="pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-600 mb-0.5">Created</p>
                    <p className="text-sm text-gray-900">
                        {order.createdAt ? <RelativeTime timestamp={order.createdAt} /> : 'N/A'}
                    </p>
                </div>
            </div>
        </div>
    );

    // Order List Item Component for List View
    const OrderListItem = ({ order }: { order: Order }) => (
        <div className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 p-4 border border-gray-100">
            <div className="flex items-center gap-4">
                {/* Status & Order Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {getStatusIcon(order.status)}
                            {OrderStatus[order.status]}
                        </span>
                        <span className="font-mono text-xs text-gray-500 truncate">{order.uuid}</span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-7 gap-4 text-sm">
                        <div>
                            <p className="text-gray-600">Customer</p>
                            <Link
                                to={`/show-lease/${order.customer?.uuid}`}
                                onClick={() => handleShowleasesClick(order.customer)}
                                className="text-indigo-600 hover:text-indigo-800 font-medium"
                            >
                                {order.customer?.mobileNumber || 'Unknown'}
                            </Link>
                        </div>
                        <div>
                            <p className="text-gray-600">Product</p>
                            <p className="font-medium">{order.productName}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Price</p>
                            <p className="font-bold"><CurrencyFormatter priceInCents={order.priceInCents} /></p>
                        </div>
                        <div>
                            <p className="text-gray-600">Rental Start</p>
                            <p className="text-gray-900">
                                {order.createdAt ? <RelativeTime timestamp={order.createdAt} /> : 'N/A'}
                            </p>
                        </div>
                        <div>
                            <p className="text-gray-600">Auth Method</p>
                            <p className="font-medium">{getAuthMethodDisplay(order.authMethod)}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Trackable Item</p>
                            <p className="font-medium">{order.trackableItemName || 'N/A'}</p>
                        </div>
                        <div>
                            <p className="text-gray-600">Returned</p>
                            <p className="text-gray-900">
                                {order.returnedAt ? <RelativeTime timestamp={order.returnedAt} /> : 'Not returned'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Pagination Controls Component
    const PaginationControls = () => {
        const pageNumbers = [];
        const delta = 2;
        
        for (let i = 1; i <= totalPages; i++) {
            if (i <= delta || i > totalPages - delta || (i >= currentPage - delta && i <= currentPage + delta)) {
                pageNumbers.push(i);
            } else if (pageNumbers[pageNumbers.length - 1] !== '...') {
                pageNumbers.push('...');
            }
        }

        return (
            <div className="flex items-center justify-center gap-1 flex-wrap">
                <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="First page"
                >
                    <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Previous page"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>
                
                {pageNumbers.map((num, idx) => (
                    <button
                        key={idx}
                        onClick={() => typeof num === 'number' ? handlePageChange(num) : null}
                        disabled={num === '...'}
                        className={`min-w-[40px] px-3 py-2 rounded-lg transition-colors ${
                            num === currentPage
                                ? 'bg-indigo-600 text-white font-medium'
                                : num === '...'
                                ? 'cursor-default'
                                : 'hover:bg-gray-100 text-gray-700'
                        }`}
                    >
                        {num}
                    </button>
                ))}
                
                <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Next page"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
                <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Last page"
                >
                    <ChevronsRight className="w-4 h-4" />
                </button>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header with Back Button */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate(-1)}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Go back"
                            >
                                <ArrowLeft className="w-5 h-5 text-gray-600" />
                            </button>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">Cell Transaction History</h1>
                                <p className="text-gray-600 mt-1">
                                    Machine: {state?.machine?.name || machineUuid} | 
                                    Cell: {state?.doorIdDisplay || `MDB #${mdbItemNumber}`}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                className="btn-primary flex items-center gap-2"
                                onClick={downloadCsv}
                                disabled={isLoading}
                            >
                                <Download className="w-4 h-4" />
                                Download CSV
                            </button>
                            {/* View Mode Toggle */}
                            <div className="bg-gray-100 rounded-lg p-1 flex">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`px-3 py-1.5 rounded-md transition-colors ${
                                        viewMode === 'grid' 
                                            ? 'bg-white text-gray-900 shadow-sm' 
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    Grid
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`px-3 py-1.5 rounded-md transition-colors ${
                                        viewMode === 'list' 
                                            ? 'bg-white text-gray-900 shadow-sm' 
                                            : 'text-gray-600 hover:text-gray-900'
                                    }`}
                                >
                                    List
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-gray-500">
                                {orders.length === 0 ? 
                                    'No transactions found' :
                                    totalOrders === -1 ?
                                        `Showing ${((currentPage - 1) * itemsPerPage) + 1} - ${((currentPage - 1) * itemsPerPage) + orders.length}` :
                                        `Showing ${((currentPage - 1) * itemsPerPage) + 1} - ${Math.min(currentPage * itemsPerPage, totalOrders)} of ${totalOrders} transactions`
                                }
                            </p>
                        </div>
                        <div className="p-4 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                            <FileText className="w-8 h-8 text-white" />
                        </div>
                    </div>
                </div>

                {/* Content */}
                {isLoading ? (
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
                            <p className="text-gray-600">Loading transactions...</p>
                        </div>
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Transactions</h2>
                        <p className="text-gray-600">{error}</p>
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-gray-900 mb-2">No Transactions Found</h2>
                        <p className="text-gray-600">No transactions have been recorded for this cell yet.</p>
                    </div>
                ) : (
                    <>
                        {viewMode === 'grid' ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                                {orders.map((order) => (
                                    <OrderCard key={order.uuid} order={order} />
                                ))}
                            </div>
                        ) : (
                            <div className="space-y-3 mb-8">
                                {orders.map((order) => (
                                    <OrderListItem key={order.uuid} order={order} />
                                ))}
                            </div>
                        )}
                        
                        {/* Pagination */}
                        {totalPages > 1 && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
                                <PaginationControls />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CellTransactionHistory;